import { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  Modal,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import MapView, { Marker, Region, MapPressEvent } from "react-native-maps";
import * as Location from "expo-location";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Location data structure
 */
interface LocationData {
  address: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Props for LocationPicker
 */
interface LocationPickerProps {
  value: string;
  onChange: (location: string, coordinates?: { latitude: number; longitude: number }) => void;
  placeholder?: string;
}

/**
 * Default region (Netherlands center)
 */
const DEFAULT_REGION: Region = {
  latitude: 52.1326,
  longitude: 5.2913,
  latitudeDelta: 3.5,
  longitudeDelta: 3.5,
};

/**
 * LocationPicker Component
 * A text input with map picker for selecting locations
 */
export const LocationPicker = ({
  value,
  onChange,
  placeholder = "Voeg locatie toe",
}: LocationPickerProps) => {
  const colors = useThemeColors();
  const mapRef = useRef<MapView>(null);

  const [showMapModal, setShowMapModal] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [region, setRegion] = useState<Region>(DEFAULT_REGION);

  /**
   * Request location permissions and get current location
   */
  const getCurrentLocation = useCallback(async () => {
    setIsLoadingLocation(true);

    try {
      const { status } = await Location.requestForegroundPermissionsAsync();

      if (status !== "granted") {
        Alert.alert(
          "Locatie toestemming",
          "Geef toestemming om je locatie te gebruiken"
        );
        setIsLoadingLocation(false);
        return;
      }

      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });

      const newRegion: Region = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      };

      setRegion(newRegion);
      mapRef.current?.animateToRegion(newRegion, 500);

      // Reverse geocode to get address
      const addresses = await Location.reverseGeocodeAsync({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });

      if (addresses.length > 0) {
        const addr = addresses[0];
        const addressStr = [
          addr.street,
          addr.streetNumber,
          addr.city,
          addr.country,
        ]
          .filter(Boolean)
          .join(", ");

        setSelectedLocation({
          address: addressStr,
          coordinates: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
          },
        });
      }
    } catch (error) {
      console.error("Error getting location:", error);
      Alert.alert("Fout", "Kon locatie niet ophalen");
    } finally {
      setIsLoadingLocation(false);
    }
  }, []);

  /**
   * Handle map press to select location
   */
  const handleMapPress = useCallback(async (event: MapPressEvent) => {
    const { coordinate } = event.nativeEvent;
    setIsLoadingLocation(true);

    try {
      // Reverse geocode the selected coordinates
      const addresses = await Location.reverseGeocodeAsync({
        latitude: coordinate.latitude,
        longitude: coordinate.longitude,
      });

      if (addresses.length > 0) {
        const addr = addresses[0];
        const addressStr = [
          addr.street,
          addr.streetNumber,
          addr.city,
          addr.country,
        ]
          .filter(Boolean)
          .join(", ");

        setSelectedLocation({
          address: addressStr || `${coordinate.latitude.toFixed(5)}, ${coordinate.longitude.toFixed(5)}`,
          coordinates: {
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
          },
        });
      } else {
        setSelectedLocation({
          address: `${coordinate.latitude.toFixed(5)}, ${coordinate.longitude.toFixed(5)}`,
          coordinates: {
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
          },
        });
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setSelectedLocation({
        address: `${coordinate.latitude.toFixed(5)}, ${coordinate.longitude.toFixed(5)}`,
        coordinates: {
          latitude: coordinate.latitude,
          longitude: coordinate.longitude,
        },
      });
    } finally {
      setIsLoadingLocation(false);
    }
  }, []);

  /**
   * Search for address
   */
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsLoadingLocation(true);

    try {
      const results = await Location.geocodeAsync(searchQuery);

      if (results.length > 0) {
        const location = results[0];
        const newRegion: Region = {
          latitude: location.latitude,
          longitude: location.longitude,
          latitudeDelta: 0.01,
          longitudeDelta: 0.01,
        };

        setRegion(newRegion);
        mapRef.current?.animateToRegion(newRegion, 500);

        setSelectedLocation({
          address: searchQuery,
          coordinates: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
        });
      } else {
        Alert.alert("Niet gevonden", "Locatie niet gevonden. Probeer een ander adres.");
      }
    } catch (error) {
      console.error("Error geocoding:", error);
      Alert.alert("Fout", "Kon locatie niet zoeken");
    } finally {
      setIsLoadingLocation(false);
    }
  }, [searchQuery]);

  /**
   * Confirm selected location
   */
  const handleConfirm = useCallback(() => {
    if (selectedLocation) {
      onChange(selectedLocation.address, selectedLocation.coordinates);
    }
    setShowMapModal(false);
    setSearchQuery("");
  }, [selectedLocation, onChange]);

  /**
   * Open map modal
   */
  const openMapPicker = useCallback(() => {
    setShowMapModal(true);
    if (value) {
      setSelectedLocation({ address: value });
    }
  }, [value]);

  return (
    <>
      {/* Location Input */}
      <TouchableOpacity
        onPress={openMapPicker}
        className="flex-row items-center px-4 py-3 rounded-xl"
        style={{ backgroundColor: colors.surfaceSecondary }}
        accessibilityLabel="Selecteer locatie"
      >
        <Ionicons name="location" size={20} color={colors.primary} />
        <Text
          className="flex-1 ml-3"
          style={{ color: value ? colors.text : colors.textTertiary }}
          numberOfLines={1}
        >
          {value || placeholder}
        </Text>
        <Ionicons name="map" size={18} color={colors.textSecondary} />
      </TouchableOpacity>

      {/* Map Modal */}
      <Modal
        visible={showMapModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowMapModal(false)}
      >
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowMapModal(false)}>
              <Text style={{ color: colors.primary }}>Annuleer</Text>
            </TouchableOpacity>
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              Kies Locatie
            </Text>
            <TouchableOpacity onPress={handleConfirm} disabled={!selectedLocation}>
              <Text
                className="font-semibold"
                style={{ color: selectedLocation ? colors.primary : colors.textTertiary }}
              >
                Bevestig
              </Text>
            </TouchableOpacity>
          </View>

          {/* Search Bar */}
          <View className="px-4 py-3">
            <View
              className="flex-row items-center px-4 py-2 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="search" size={18} color={colors.textSecondary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Zoek adres..."
                placeholderTextColor={colors.textTertiary}
                className="flex-1 ml-2"
                style={{ color: colors.text }}
                returnKeyType="search"
                onSubmitEditing={handleSearch}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Map */}
          <View className="flex-1">
            <MapView
              ref={mapRef}
              style={StyleSheet.absoluteFillObject}
              region={region}
              onRegionChangeComplete={setRegion}
              onPress={handleMapPress}
              showsUserLocation
              showsMyLocationButton={false}
            >
              {selectedLocation?.coordinates && (
                <Marker
                  coordinate={selectedLocation.coordinates}
                  title={selectedLocation.address}
                />
              )}
            </MapView>

            {/* Loading Overlay */}
            {isLoadingLocation && (
              <View
                className="absolute inset-0 items-center justify-center"
                style={{ backgroundColor: colors.overlay }}
              >
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            )}

            {/* Current Location Button */}
            <TouchableOpacity
              onPress={getCurrentLocation}
              className="absolute right-4 top-4 w-12 h-12 rounded-full items-center justify-center shadow-lg"
              style={{ backgroundColor: colors.surface }}
              accessibilityLabel="Huidige locatie"
            >
              <Ionicons name="locate" size={24} color={colors.primary} />
            </TouchableOpacity>
          </View>

          {/* Selected Location Info */}
          {selectedLocation && (
            <View
              className="px-4 py-4 border-t"
              style={{ borderColor: colors.border, backgroundColor: colors.surface }}
            >
              <View className="flex-row items-center">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primaryLight }}
                >
                  <Ionicons name="pin" size={20} color={colors.primary} />
                </View>
                <View className="flex-1 ml-3">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    Geselecteerde locatie
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    {selectedLocation.address}
                  </Text>
                </View>
              </View>
            </View>
          )}
        </SafeAreaView>
      </Modal>
    </>
  );
};

export default LocationPicker;

